import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import { API_URL } from "../config";

interface Promotion {
  id: number;
  title: string;
  description: string;
  service_type: 'trip' | 'visa_pack' | 'car_rental';
  service_id: number;
  original_price: number;
  promotional_price: number;
  discount_percentage: number;
  start_date: string;
  end_date: string;
  is_active: boolean;
  max_uses: number | null;
  used_count: number;
  promo_code: string;
  terms_conditions: string | null;
  created_at: string;
  updated_at: string;
  service_details: {
    id: number;
    name: string;
    price: number;
    original_data?: any;
  };
}

interface IconProps {
  size?: number;
  color?: string;
}

const iconBase = (size: number) => ({
  width: size,
  height: size,
  viewBox: "0 0 24 24",
  fill: "none" as const,
  strokeWidth: 2,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
});

const SearchIcon: React.FC<IconProps> = ({ size = 16, color = "currentColor" }) => (
  <svg {...iconBase(size)} stroke={color}>
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const XIcon: React.FC<IconProps> = ({ size = 16, color = "currentColor" }) => (
  <svg {...iconBase(size)} stroke={color}>
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const PlusIcon: React.FC<IconProps> = ({ size = 16, color = "currentColor" }) => (
  <svg {...iconBase(size)} stroke={color}>
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const EditIcon: React.FC<IconProps> = ({ size = 16, color = "currentColor" }) => (
  <svg {...iconBase(size)} stroke={color}>
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const TrashIcon: React.FC<IconProps> = ({ size = 15, color = "currentColor" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 16 16" fill={color}>
    <path d="M 6.496094 1 C 5.675781 1 5 1.675781 5 2.496094 L 5 3 L 2 3 L 2 4 L 3 4 L 3 12.5 C 3 13.328125 3.671875 14 4.5 14 L 10.5 14 C 11.328125 14 12 13.328125 12 12.5 L 12 4 L 13 4 L 13 3 L 10 3 L 10 2.496094 C 10 1.675781 9.324219 1 8.503906 1 Z M 6.496094 2 L 8.503906 2 C 8.785156 2 9 2.214844 9 2.496094 L 9 3 L 6 3 L 6 2.496094 C 6 2.214844 6.214844 2 6.496094 2 Z M 5 5 L 6 5 L 6 12 L 5 12 Z M 7 5 L 8 5 L 8 12 L 7 12 Z M 9 5 L 10 5 L 10 12 L 9 12 Z" />
  </svg>
);

const PowerIcon: React.FC<IconProps> = ({ size = 16, color = "currentColor" }) => (
  <svg {...iconBase(size)} stroke={color}>
    <path d="M18.36 6.64a9 9 0 1 1-12.73 0" />
    <line x1="12" y1="2" x2="12" y2="12" />
  </svg>
);

const ChevronLeftIcon: React.FC<IconProps> = ({ size = 16, color = "currentColor" }) => (
  <svg {...iconBase(size)} stroke={color}>
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

const ChevronRightIcon: React.FC<IconProps> = ({ size = 16, color = "currentColor" }) => (
  <svg {...iconBase(size)} stroke={color}>
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

const colors = {
  primary: "#294b5a",
  primaryHover: "#1a3641",
  text: "#1e293b",
  textMuted: "#5f6b76",
  textFaint: "#94a3b8",
  border: "#dbe0e4",
  borderLight: "#e5e7eb",
  bgSubtle: "#f8fafc",
  danger: "#ef4444",
  success: "#10b981",
};

const cardStyle: React.CSSProperties = {
  background: "#fff",
  border: `0.5px solid ${colors.borderLight}`,
  borderRadius: 12,
};

const buttonBase: React.CSSProperties = {
  padding: "8px 14px",
  borderRadius: 8,
  fontSize: 13,
  fontWeight: 500,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 6,
  border: "1px solid transparent",
  transition: "all .15s ease",
};

const ListPromotion = () => {
  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [serviceTypeFilter, setServiceTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const navigate = useNavigate();

  const fetchPromotions = async (page = 1) => {
    try {
      setIsLoading(true);
      const params = new URLSearchParams();

      if (searchTerm) params.append("search", searchTerm);
      if (serviceTypeFilter) params.append("service_type", serviceTypeFilter);
      if (statusFilter) params.append("is_active", statusFilter);
      params.append("page", page.toString());

      const response = await axios.get(`${API_URL}/promotions?${params}`);

      if (response.data.status) {
        setPromotions(response.data.data.data || response.data.data || []);
        setCurrentPage(response.data.data.current_page || 1);
        setLastPage(response.data.data.last_page || 1);
      }
    } catch (error) {
      console.error("Error fetching promotions:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPromotions();
  }, []);

  const filteredPromotions = useMemo(() => {
    if (!searchTerm && !serviceTypeFilter && !statusFilter) return promotions;

    const lowercasedSearchTerm = searchTerm.toLowerCase();

    return promotions
      .filter(
        (promotion) =>
          promotion.title.toLowerCase().includes(lowercasedSearchTerm) ||
          promotion.promo_code.toLowerCase().includes(lowercasedSearchTerm) ||
          promotion.service_details?.name?.toLowerCase().includes(lowercasedSearchTerm)
      )
      .filter((promotion) => {
        if (serviceTypeFilter && promotion.service_type !== serviceTypeFilter) return false;
        if (statusFilter && promotion.is_active.toString() !== statusFilter) return false;
        return true;
      });
  }, [promotions, searchTerm, serviceTypeFilter, statusFilter]);

  const handleDeleteClick = (rowIndex: number) => {
    setSelectedRow(rowIndex);
    setShowDeleteModal(true);
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setSelectedRow(null);
  };

  const handleConfirmDelete = async () => {
    if (selectedRow === null) return;

    try {
      const promotionToDelete = filteredPromotions[selectedRow];
      await axios.delete(`${API_URL}/promotions/${promotionToDelete.id}`);

      toast.success("Promotion deleted successfully");
      fetchPromotions(currentPage);
    } catch (error: any) {
      console.error("Error deleting promotion:", error.response || error.message);
    } finally {
      setShowDeleteModal(false);
      setSelectedRow(null);
    }
  };

  const handleEditClick = (id: number) => {
    navigate(`/admin/edit-promotion/${id}`);
  };

  const handleToggleStatus = async (promotion: Promotion) => {
    try {
      const response = await axios.put(`${API_URL}/promotions/${promotion.id}/toggle-status`);

      if (response.data.status) {
        toast.success(`Promotion ${!promotion.is_active ? "activated" : "deactivated"} successfully`);
        fetchPromotions(currentPage);
      }
    } catch (error: any) {
      console.error("Error updating promotion status:", error.response?.data || error.message);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const clearSearch = () => {
    setSearchTerm("");
    setServiceTypeFilter("");
    setStatusFilter("");
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusClass = (isActive: boolean) => {
    return isActive
      ? { bg: "#dcfce7", text: "#15803d" }
      : { bg: "#fee2e2", text: "#b91c1c" };
  };

  const getServiceTypeLabel = (serviceType: string) => {
    switch (serviceType) {
      case "trip":
        return "Trip";
      case "visa_pack":
        return "Visa Pack";
      case "car_rental":
        return "Car Rental";
      default:
        return serviceType;
    }
  };

  const isPromotionExpired = (endDate: string) => new Date(endDate) < new Date();
  const isPromotionUpcoming = (startDate: string) => new Date(startDate) > new Date();

  const getPromotionStatus = (promotion: Promotion) => {
    if (!promotion.is_active) return "Inactive";
    if (isPromotionExpired(promotion.end_date)) return "Expired";
    if (isPromotionUpcoming(promotion.start_date)) return "Upcoming";
    return "Active";
  };

  return (
    <>
      {/* Grille : Left (20%), Center (60%), Right (20%) */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "20% 60% 20%",
          gap: "16px",
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        {/* Colonne Gauche (20%) - Filtres de sélection */}
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          <div style={{ ...cardStyle, padding: 16 }}>
            <h3 style={{ fontSize: 14, fontWeight: 600, margin: "0 0 12px 0", color: colors.text }}>
              Filter Promotions
            </h3>

            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div>
                <label style={{ display: "block", fontSize: 12, fontWeight: 500, color: colors.textMuted, marginBottom: 6 }}>
                  Service Type
                </label>
                <select
                  value={serviceTypeFilter}
                  onChange={(e) => setServiceTypeFilter(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "8px 10px",
                    border: `1px solid ${colors.border}`,
                    borderRadius: 8,
                    fontSize: 12,
                    color: colors.text,
                    background: colors.bgSubtle,
                    outline: "none",
                  }}
                >
                  <option value="">All Services</option>
                  <option value="trip">Trip</option>
                  <option value="visa_pack">Visa Pack</option>
                  <option value="car_rental">Car Rental</option>
                </select>
              </div>

              <div>
                <label style={{ display: "block", fontSize: 12, fontWeight: 500, color: colors.textMuted, marginBottom: 6 }}>
                  Status
                </label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "8px 10px",
                    border: `1px solid ${colors.border}`,
                    borderRadius: 8,
                    fontSize: 12,
                    color: colors.text,
                    background: colors.bgSubtle,
                    outline: "none",
                  }}
                >
                  <option value="">All Status</option>
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </select>
              </div>

              <button
                type="button"
                onClick={() => fetchPromotions()}
                style={{
                  ...buttonBase,
                  background: colors.primary,
                  color: "#fff",
                  marginTop: 4,
                }}
              >
                Apply Filters
              </button>
            </div>
          </div>
        </div>

        {/* Colonne Centre (60%) - Contenu Principal */}
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          {/* Section Header & Recherche */}
          <div style={{ ...cardStyle, padding: 16 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 12,
              }}
            >
              <div>
                <h1 style={{ fontSize: 18, fontWeight: 600, margin: 0, color: colors.text }}>
                  Manage Promotions
                </h1>
                <span style={{ fontSize: 12, color: colors.textMuted }}>
                  {promotions.length} total promotions
                  {searchTerm && ` • ${filteredPromotions.length} filtered`}
                </span>
              </div>

              <a
                href="/admin/add-promotion"
                style={{
                  ...buttonBase,
                  background: colors.primary,
                  color: "#fff",
                  textDecoration: "none",
                }}
              >
                <PlusIcon size={14} color="#fff" /> Add Promotion
              </a>
            </div>

            {/* Barre de recherche */}
            <div style={{ position: "relative", width: "100%" }}>
              <span
                style={{
                  position: "absolute",
                  left: 12,
                  top: "50%",
                  transform: "translateY(-50%)",
                  display: "flex",
                  color: colors.textFaint,
                }}
              >
                <SearchIcon size={16} />
              </span>
              <input
                type="text"
                placeholder="Search promotions by title, promo code, service..."
                value={searchTerm}
                onChange={handleSearchChange}
                style={{
                  width: "100%",
                  boxSizing: "border-box",
                  padding: "9px 36px 9px 36px",
                  border: `1px solid ${colors.border}`,
                  borderRadius: 8,
                  fontSize: 13,
                  color: colors.text,
                  background: colors.bgSubtle,
                  outline: "none",
                  transition: "border-color .15s ease, background .15s ease",
                }}
                onFocus={(e) => {
                  e.target.style.background = "#fff";
                  e.target.style.borderColor = colors.primary;
                }}
                onBlur={(e) => {
                  e.target.style.background = colors.bgSubtle;
                  e.target.style.borderColor = colors.border;
                }}
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={clearSearch}
                  style={{
                    position: "absolute",
                    right: 10,
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: colors.textFaint,
                    display: "flex",
                    padding: 2,
                  }}
                >
                  <XIcon size={14} />
                </button>
              )}
            </div>
          </div>

          {/* Tableau */}
          <div style={{ ...cardStyle, overflow: "hidden" }}>
            <div style={{ overflowX: "auto" }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: 13,
                  textAlign: "left",
                }}
              >
                <thead>
                  <tr
                    style={{
                      background: colors.bgSubtle,
                      borderBottom: `1px solid ${colors.borderLight}`,
                    }}
                  >
                    <th style={{ padding: "12px 14px", fontWeight: 600, color: colors.textMuted }}>Promotion</th>
                    <th style={{ padding: "12px 14px", fontWeight: 600, color: colors.textMuted }}>Service</th>
                    <th style={{ padding: "12px 14px", fontWeight: 600, color: colors.textMuted }}>Code</th>
                    <th style={{ padding: "12px 14px", fontWeight: 600, color: colors.textMuted }}>Prices</th>
                    <th style={{ padding: "12px 14px", fontWeight: 600, color: colors.textMuted }}>Validity</th>
                    <th style={{ padding: "12px 14px", fontWeight: 600, color: colors.textMuted }}>Status</th>
                    <th style={{ padding: "12px 14px", fontWeight: 600, color: colors.textMuted, textAlign: "center" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr>
                      <td colSpan={7} style={{ textAlign: "center", padding: "40px 16px", color: colors.textFaint }}>
                        Loading promotions...
                      </td>
                    </tr>
                  ) : filteredPromotions.length > 0 ? (
                    filteredPromotions.map((promotion, index) => {
                      const statusClass = getStatusClass(promotion.is_active);
                      const promotionStatus = getPromotionStatus(promotion);

                      return (
                        <tr
                          key={promotion.id}
                          style={{
                            borderBottom: `1px solid ${colors.borderLight}`,
                            transition: "background .15s ease",
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = colors.bgSubtle)}
                          onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                        >
                          <td style={{ padding: "12px 14px" }}>
                            <div style={{ fontWeight: 500, color: colors.text }}>{promotion.title}</div>
                            <div style={{ fontSize: 11, color: colors.textFaint }}>
                              {promotion.description || "No description"}
                            </div>
                          </td>
                          <td style={{ padding: "12px 14px" }}>
                            <div style={{ fontWeight: 500, color: colors.text }}>{promotion.service_details?.name || "N/A"}</div>
                            <span
                              style={{
                                display: "inline-block",
                                padding: "2px 6px",
                                borderRadius: 8,
                                fontSize: 10,
                                fontWeight: 500,
                                background: "#eff6ff",
                                color: "#1e40af",
                              }}
                            >
                              {getServiceTypeLabel(promotion.service_type)}
                            </span>
                          </td>
                          <td style={{ padding: "12px 14px", fontWeight: 600, fontFamily: "monospace", color: colors.text }}>
                            {promotion.promo_code}
                          </td>
                          <td style={{ padding: "12px 14px" }}>
                            <div style={{ fontSize: 11, textDecoration: "line-through", color: colors.textFaint }}>
                              ${promotion.original_price}
                            </div>
                            <div style={{ fontWeight: 600, color: colors.danger, fontSize: 12 }}>
                              ${promotion.promotional_price}
                              <span style={{ fontSize: 10, color: colors.success, marginLeft: 4 }}>
                                ({promotion.discount_percentage}% OFF)
                              </span>
                            </div>
                          </td>
                          <td style={{ padding: "12px 14px", fontSize: 11, color: colors.textMuted }}>
                            <div>Start: {formatDate(promotion.start_date)}</div>
                            <div>End: {formatDate(promotion.end_date)}</div>
                          </td>
                          <td style={{ padding: "12px 14px" }}>
                            <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                              <span
                                style={{
                                  display: "inline-block",
                                  padding: "2px 8px",
                                  borderRadius: 12,
                                  fontSize: 10,
                                  fontWeight: 500,
                                  textAlign: "center",
                                  background: statusClass.bg,
                                  color: statusClass.text,
                                }}
                              >
                                {promotion.is_active ? "ACTIVE" : "INACTIVE"}
                              </span>
                              <span
                                style={{
                                  display: "inline-block",
                                  padding: "2px 6px",
                                  borderRadius: 8,
                                  fontSize: 9,
                                  fontWeight: 500,
                                  textAlign: "center",
                                  background:
                                    promotionStatus === "Expired"
                                      ? "#fee2e2"
                                      : promotionStatus === "Upcoming"
                                      ? "#fef3c7"
                                      : "#dcfce7",
                                  color:
                                    promotionStatus === "Expired"
                                      ? "#b91c1c"
                                      : promotionStatus === "Upcoming"
                                      ? "#b45309"
                                      : "#15803d",
                                }}
                              >
                                {promotionStatus.toUpperCase()}
                              </span>
                            </div>
                          </td>
                          <td style={{ padding: "12px 14px", textAlign: "center" }}>
                            <div style={{ display: "flex", justifyContent: "center", gap: 6 }}>
                              <button
                                type="button"
                                onClick={() => handleToggleStatus(promotion)}
                                title={promotion.is_active ? "Deactivate" : "Activate"}
                                style={{
                                  background: "none",
                                  border: "none",
                                  color: promotion.is_active ? colors.danger : colors.success,
                                  cursor: "pointer",
                                  padding: 4,
                                  display: "flex",
                                  borderRadius: 4,
                                }}
                              >
                                <PowerIcon size={15} />
                              </button>
                              <button
                                type="button"
                                title="Edit"
                                onClick={() => handleEditClick(promotion.id)}
                                style={{
                                  background: "none",
                                  border: "none",
                                  color: colors.primary,
                                  cursor: "pointer",
                                  padding: 4,
                                  display: "flex",
                                  borderRadius: 4,
                                }}
                              >
                                <EditIcon size={15} />
                              </button>
                              <button
                                type="button"
                                title="Delete"
                                onClick={() => handleDeleteClick(index)}
                                style={{
                                  background: "none",
                                  border: "none",
                                  color: colors.danger,
                                  cursor: "pointer",
                                  padding: 4,
                                  display: "flex",
                                  borderRadius: 4,
                                }}
                              >
                                <TrashIcon size={14} color={colors.danger} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={7} style={{ textAlign: "center", padding: "40px 16px", color: colors.textFaint }}>
                        <div style={{ fontSize: 14, marginBottom: 6 }}>
                          {searchTerm ? "No promotions found matching your search." : "No promotions found."}
                        </div>
                        {(searchTerm || serviceTypeFilter || statusFilter) && (
                          <button
                            type="button"
                            onClick={clearSearch}
                            style={{
                              background: "none",
                              border: "none",
                              color: colors.primary,
                              cursor: "pointer",
                              fontSize: 12,
                              textDecoration: "underline",
                            }}
                          >
                            Clear filters
                          </button>
                        )}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {promotions.length > 0 && !searchTerm && (
              <div
                style={{
                  padding: "12px 16px",
                  borderTop: `1px solid ${colors.borderLight}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  background: "#fff",
                }}
              >
                <span style={{ fontSize: 12, color: colors.textMuted }}>
                  Page {currentPage} of {lastPage}
                </span>
                <div style={{ display: "flex", gap: 4 }}>
                  <button
                    type="button"
                    onClick={() => {
                      if (currentPage > 1) fetchPromotions(currentPage - 1);
                    }}
                    disabled={currentPage === 1}
                    style={{
                      ...buttonBase,
                      padding: "6px 10px",
                      background: colors.bgSubtle,
                      borderColor: colors.border,
                      color: colors.text,
                      opacity: currentPage === 1 ? 0.4 : 1,
                      cursor: currentPage === 1 ? "not-allowed" : "pointer",
                    }}
                  >
                    <ChevronLeftIcon size={14} />
                  </button>

                  {Array.from({ length: Math.min(5, lastPage) }, (_, i) => {
                    let page: number;
                    if (lastPage <= 5) page = i + 1;
                    else if (currentPage <= 3) page = i + 1;
                    else if (currentPage >= lastPage - 2) page = lastPage - 4 + i;
                    else page = currentPage - 2 + i;

                    const active = currentPage === page;
                    return (
                      <button
                        key={page}
                        type="button"
                        onClick={() => fetchPromotions(page)}
                        style={{
                          ...buttonBase,
                          padding: "6px 12px",
                          fontSize: 12,
                          background: active ? colors.primary : "#fff",
                          color: active ? "#fff" : colors.text,
                          borderColor: active ? colors.primary : colors.border,
                        }}
                      >
                        {page}
                      </button>
                    );
                  })}

                  <button
                    type="button"
                    onClick={() => {
                      if (currentPage < lastPage) fetchPromotions(currentPage + 1);
                    }}
                    disabled={currentPage === lastPage}
                    style={{
                      ...buttonBase,
                      padding: "6px 10px",
                      background: colors.bgSubtle,
                      borderColor: colors.border,
                      color: colors.text,
                      opacity: currentPage === lastPage ? 0.4 : 1,
                      cursor: currentPage === lastPage ? "not-allowed" : "pointer",
                    }}
                  >
                    <ChevronRightIcon size={14} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Colonne Droite (20%) - Synthèse et statistiques */}
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          <div
            style={{
              ...cardStyle,
              padding: 16,
              display: "flex",
              flexDirection: "column",
              gap: 12,
            }}
          >
            <h3 style={{ fontSize: 14, fontWeight: 600, margin: 0, color: colors.text }}>Summary</h3>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 8,
                fontSize: 12,
                color: colors.textMuted,
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span>Showing:</span>
                <span style={{ fontWeight: 600, color: colors.text }}>
                  {filteredPromotions.length} items
                </span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span>Total pages:</span>
                <span style={{ fontWeight: 600, color: colors.text }}>{lastPage}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de suppression */}
      {showDeleteModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.4)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: "12px",
              width: "420px",
              maxWidth: "90%",
              overflow: "hidden",
              boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1)",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "16px 20px",
                borderBottom: `1px solid ${colors.borderLight}`,
              }}
            >
              <h5 style={{ fontSize: 15, fontWeight: 600, color: colors.text, margin: 0 }}>
                Confirm Deletion
              </h5>
              <button
                type="button"
                onClick={handleCancelDelete}
                style={{
                  border: "none",
                  background: "transparent",
                  cursor: "pointer",
                  fontSize: "1.2rem",
                  color: colors.textFaint,
                }}
              >
                &times;
              </button>
            </div>

            <div style={{ padding: "20px", fontSize: 13, color: colors.textMuted }}>
              Are you sure you want to delete this promotion? This action cannot be undone.
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "8px",
                padding: "12px 20px",
                borderTop: `1px solid ${colors.borderLight}`,
                background: colors.bgSubtle,
              }}
            >
              <button
                type="button"
                onClick={handleCancelDelete}
                style={{
                  ...buttonBase,
                  background: "#fff",
                  border: `1px solid ${colors.border}`,
                  color: colors.textMuted,
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmDelete}
                style={{
                  ...buttonBase,
                  background: colors.danger,
                  color: "#fff",
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer />
    </>
  );
};

export default ListPromotion;